import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stack,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  WorkOutline as WorkIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  AttachMoney as FeeIcon,
  Build as EquipmentIcon,
  Approval as CertIcon,
  History as HistoryIcon,
  TrendingUp as ExperienceIcon,
  Edit as EditIcon,
  Person as PersonIcon
} from '@mui/icons-material';

interface Personnel {
  _id: string;
  name: string;
  email: string;
  phone: string;
  roles: string[];
  skillLevel: string;
  experience: number;
  profileImage?: string;
  bio?: string;
  availability?: {
    from: string | Date;
    to: string | Date;
  };
  fee: number;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  equipment?: string[];
  certifications?: string[];
  reviews?: Array<{
    rating: number;
    comment: string;
    date: Date;
    reviewer: string;
  }>;
  performances?: string[];
  createdAt: string;
  updatedAt: string;
}

interface Performance {
  _id: string;
  simpleBandName?: string;
  band?: string | {
    _id: string;
    artistName: string;
  };
  startTime: string;
  staff: Array<{ staffId: string; assignedRole: string; }>;
  venue?: string | { name: string };
  status?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const PerformanceCard: React.FC<{ performance: Performance; staffId: string }> = ({ performance, staffId }) => {
  const navigate = useNavigate();

  const getBandName = (perf: Performance): string => {
    if (perf.simpleBandName) return perf.simpleBandName;
    if (perf.band) {
      if (typeof perf.band === 'string') return 'Loading...';
      return perf.band.artistName;
    }
    return 'Unknown Artist';
  };

  const getAssignedRole = () => {
    const staffMember = performance.staff.find(s => s.staffId === staffId);
    return staffMember?.assignedRole || 'Role not specified';
  };

  const isPast = new Date(performance.startTime) < new Date();

  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        },
        height: '100%',
        border: isPast ? '1px solid #e0e0e0' : '1px solid #1976d2',
        opacity: isPast ? 0.8 : 1
      }}
      onClick={() => navigate(`/admin/performances/${performance._id}`)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {getBandName(performance)}
          </Typography>
          <Chip 
            label={isPast ? 'Past' : 'Upcoming'} 
            size="small" 
            color={isPast ? 'default' : 'primary'}
            variant="outlined"
          />
        </Box>
        
        <Typography color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon fontSize="small" />
          {new Date(performance.startTime).toLocaleDateString('en-IE', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </Typography>
        
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkIcon fontSize="small" />
          <strong>Role:</strong> {getAssignedRole()}
        </Typography>
        
        {performance.venue && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon fontSize="small" />
           {typeof performance.venue === 'string' ? performance.venue : performance.venue.name}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const PersonnelDetail: React.FC<{ id: string }> = ({ id }) => {
  const [person, setPerson] = useState<Personnel | null>(null);
  const [performances, setPerformances] = useState<Performance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [personResponse, performancesResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/personnel/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/performances/staff/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          })
        ]);
        
        if (!personResponse.ok) {
          throw new Error(`Failed to fetch personnel: ${personResponse.status}`);
        }
        
        if (!performancesResponse.ok) {
          throw new Error(`Failed to fetch performances: ${performancesResponse.status}`);
        }
        
        const personData = await personResponse.json();
        const performancesResult = await performancesResponse.json();
        
        setPerson(personData);
        
        const performanceArray = performancesResult.success 
          ? (Array.isArray(performancesResult.data) ? performancesResult.data : [])
          : (Array.isArray(performancesResult) ? performancesResult : []);
          
        setPerformances(performanceArray);

        console.log('Query results:', {
  totalFound: performanceArray.length,
  performances: performanceArray.map((p: Performance) => ({
    id: p._id,
    bandName: p.simpleBandName || (typeof p.band === 'string' ? p.band : p.band?.artistName),
    startTime: p.startTime,
    staffIds: p.staff?.map(s => s.staffId)
  }))
});

        // ADD THESE LINES HERE:
console.log('All performances:', performanceArray);
console.log('Today:', new Date());

performanceArray.forEach((perf: Performance, index: number) => {
  console.log(`Performance ${index}:`, {
    startTime: perf.startTime,
    parsedDate: new Date(perf.startTime),
    isPast: new Date(perf.startTime) < new Date()
  });
});
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
        <Typography sx={{ ml: 2 }}>Loading personnel details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!person) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No personnel found.
      </Alert>
    );
  }

const roleColors: Record<string, { bg: string; text: string }> = {
 'Sound Engineer': { bg: '#4A3B66', text: 'white' },
 'Lighting Engineer': { bg: '#4A2D6B', text: 'white' },
 'Stage Manager': { bg: '#2D4A6B', text: 'white' },
 'Front of House': { bg: '#6B5A2D', text: 'white' },
 'Runner': { bg: '#3B5C4A', text: 'white' },
 'Production Manager': { bg: '#6B3B3B', text: 'white' },
 'Stage Hand': { bg: '#2D4A66', text: 'white' },
 'Artist Liaison': { bg: '#6B2D4A', text: 'white' },
 'Photographer/Videographer': { bg: '#5C4A2D', text: 'white' },
 'Staff': { bg: '#4A5C3B', text: 'white' },
 'Vision Mix Engineer': { bg: '#5A2D6B', text: 'white' },
 'Volunteer': { bg: '#2D5C5A', text: 'white' },
 'Structural Engineer': { bg: '#4A4A5C', text: 'white' },
 'Backline Supplier': { bg: '#5C3B2D', text: 'white' },
 'Supplier': { bg: '#5C2D3B', text: 'white' }
};

  const getSkillLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'expert': return '#4caf50';
      case 'advanced': return '#ff9800';
      case 'intermediate': return '#2196f3';
      case 'beginner': return '#9e9e9e';
      default: return '#9e9e9e';
    }
  };

  const formatAddress = (address: Personnel['address']) => {
    if (!address) return null;
    const parts = [
      address.street,
      address.city,
      address.state,
      address.postalCode,
      address.country
    ].filter(Boolean);
    return parts.join(', ');
  };

  const getAverageRating = () => {
    if (!person.reviews || person.reviews.length === 0) return 0;
    const sum = person.reviews.reduce((acc, review) => acc + review.rating, 0);
    return sum / person.reviews.length;
  };

  const today = new Date();
  const pastPerformances = performances.filter(p => new Date(p.startTime) < today);
  const upcomingPerformances = performances.filter(p => new Date(p.startTime) >= today);

  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 3 }}>
      {/* Header Card */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Avatar sx={{ bgcolor: getSkillLevelColor(person.skillLevel), width: 32, height: 32 }}>
                    <StarIcon />
                  </Avatar>
                }
              >
                <Avatar 
                  src={person.profileImage} 
                  alt={person.name}
                  sx={{ width: 160, height: 160, mb: 2, border: '4px solid white' }}
                >
                  <PersonIcon sx={{ fontSize: 80 }} />
                </Avatar>
              </Badge>
              
              <Typography variant="h4" sx={{ textAlign: 'center', mb: 1, fontWeight: 'bold' }}>
                {person.name}
              </Typography>
              
              <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, opacity: 0.9 }}>
                {person.skillLevel} • {person.experience} years
              </Typography>

              {person.reviews && person.reviews.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <StarIcon sx={{ color: '#ffd700' }} />
                  <Typography variant="h6">
                    {getAverageRating().toFixed(1)}
                  </Typography>
                  <Typography variant="body2">
                    ({person.reviews.length} reviews)
                  </Typography>
                </Box>
              )}

              <Stack direction="row" spacing={1}>
                <Tooltip title="Call">
                  <IconButton 
                    href={`tel:${person.phone}`} 
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                  >
                    <PhoneIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Email">
                  <IconButton 
                    href={`mailto:${person.email}`} 
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                  >
                    <EmailIcon />
                  </IconButton>
                </Tooltip>
<Tooltip title="Edit Profile">
 <IconButton 
   component={Link} 
   to={`/admin/personnel/edit/${person._id}`}
   sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
 >
   <EditIcon />
 </IconButton>
</Tooltip>
              </Stack>
            </Grid>

            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                {/* Contact Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon /> Contact Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <PhoneIcon sx={{ color: 'white' }} />
                      </ListItemIcon>
                      <ListItemText primary={person.phone} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon sx={{ color: 'white' }} />
                      </ListItemIcon>
                      <ListItemText primary={person.email} />
                    </ListItem>
                    {person.address && (
                      <ListItem>
                        <ListItemIcon>
                          <LocationIcon sx={{ color: 'white' }} />
                        </ListItemIcon>
                        <ListItemText primary={formatAddress(person.address)} />
                      </ListItem>
                    )}
                  </List>
                </Grid>

                {/* Professional Details */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WorkIcon /> Professional Details
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ExperienceIcon sx={{ color: 'white' }} />
                      </ListItemIcon>
                      <ListItemText primary={`${person.experience} years experience`} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FeeIcon sx={{ color: 'white' }} />
                      </ListItemIcon>
                      <ListItemText primary={person.fee > 0 ? `€${person.fee} per day` : 'IMC Staff'} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <HistoryIcon sx={{ color: 'white' }} />
                      </ListItemIcon>
                      <ListItemText primary={`${performances.length} total performances`} />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Roles & Expertise */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkIcon /> Roles & Expertise
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {person.roles.map((role) => {
              const colors = roleColors[role] || { bg: '#9e9e9e', text: 'white' };
              return (
                <Chip
                  key={role}
                  label={role}
                  sx={{ 
                    bgcolor: colors.bg, 
                    color: colors.text,
                    fontWeight: 'bold'
                  }}
                />
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* Additional Information Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Equipment */}
        {person.equipment && person.equipment.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EquipmentIcon /> Equipment
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {person.equipment.map((item, index) => (
                    <Chip key={index} label={item} variant="outlined" />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Certifications */}
        {person.certifications && person.certifications.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CertIcon /> Certifications
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {person.certifications.map((cert, index) => (
                    <Chip key={index} label={cert} variant="outlined" color="success" />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Availability */}
        {person.availability && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon /> Availability
                </Typography>
                <Typography>
                  {new Date(person.availability.from).toLocaleDateString('en-IE')} 
                  {' '} - {' '}
                  {new Date(person.availability.to).toLocaleDateString('en-IE')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Bio */}
        {person.bio && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Biography
                </Typography>
                <Typography variant="body1">
                  {person.bio}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Performance History */}
      <Paper>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon />
                Performance History ({pastPerformances.length})
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon />
                Upcoming Events ({upcomingPerformances.length})
              </Box>
            } 
          />
          {person.reviews && person.reviews.length > 0 && (
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StarIcon />
                  Reviews ({person.reviews.length})
                </Box>
              } 
            />
          )}
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {pastPerformances.length > 0 ? (
            <Grid container spacing={3}>
              {pastPerformances.map((performance) => (
                <Grid item xs={12} md={6} lg={4} key={performance._id}>
                  <PerformanceCard 
                    performance={performance}
                    staffId={id}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary" variant="h6">
                No past performances found
              </Typography>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {upcomingPerformances.length > 0 ? (
            <Grid container spacing={3}>
              {upcomingPerformances.map((performance) => (
                <Grid item xs={12} md={6} lg={4} key={performance._id}>
                  <PerformanceCard 
                    performance={performance}
                    staffId={id}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary" variant="h6">
                No upcoming events scheduled
              </Typography>
            </Box>
          )}
        </TabPanel>

        {person.reviews && person.reviews.length > 0 && (
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              {person.reviews.map((review, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {[...Array(5)].map((_, i) => (
                            <StarIcon 
                              key={i} 
                              sx={{ 
                                color: i < review.rating ? '#ffd700' : '#e0e0e0',
                                fontSize: 20
                              }} 
                            />
                          ))}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(review.date).toLocaleDateString('en-IE')}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        "{review.comment}"
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        - {review.reviewer}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </TabPanel>
        )}
      </Paper>
    </Box>
  );
};

export default PersonnelDetail;